import { useMemo } from 'react';
import ExpandLessTwoTone from '@mui/icons-material/ExpandLessTwoTone';
import ExpandMoreTwoTone from '@mui/icons-material/ExpandMoreTwoTone';
import {
  alpha,
  createTheme,
  CssBaseline,
  darken,
  GlobalStyles,
  lighten,
  PaletteMode,
  ThemeOptions,
  ThemeProvider
} from '@mui/material';
import * as colors from '@mui/material/colors';
import { deepmerge } from '@mui/utils';
import { gridClasses } from '@mui/x-data-grid-pro';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import type {} from '@mui/x-data-grid-pro/themeAugmentation';

import {
  DEFAULT_THEME,
  FONT_FAMILY,
  FONT_FAMILY_DEJAVU_MONO,
  FONT_FAMILY_ROBOTO,
  FONT_FAMILY_ROBOTO_MONO,
  FONT_FAMILY_SECONDARY,
  FONT_FAMILY_SOURCE_CODE_PRO
} from '@attackiq/constants';

import '@fontsource/source-sans-pro/300.css';
import '@fontsource/source-sans-pro/400.css';
import '@fontsource/source-sans-pro/600.css';
import '@fontsource/source-sans-pro/700.css';
import '@fontsource/source-sans-pro/900.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/roboto/900.css';
import '@fontsource-variable/open-sans';
import '@fontsource-variable/roboto-mono';
import '@fontsource-variable/source-code-pro';
import '@fontsource/dejavu-mono';

const paperElevationColors: string[] = [
  '#212939', // paper-elevation-0
  '#212939', // paper-elevation-1
  '#212939', // paper-elevation-2
  '#212939', // paper-elevation-3
  '#212939', // paper-elevation-4
  '#212939', // paper-elevation-5
  '#212939', // paper-elevation-6
  // ---------------------------------
  '#242D3E', // paper-elevation-7
  '#242D3E', // paper-elevation-8
  '#242D3E', // paper-elevation-9
  '#242D3E', // paper-elevation-10
  '#242D3E', // paper-elevation-11
  '#242D3E', // paper-elevation-12
  '#242D3E', // paper-elevation-13
  '#242D3E', // paper-elevation-14
  // ---------------------------------
  '#273143', // paper-elevation-15
  '#273143', // paper-elevation-16
  '#273143', // paper-elevation-17
  '#273143', // paper-elevation-18
  '#273143', // paper-elevation-19
  // ---------------------------------
  '#2A3446', // paper-elevation-20
  // ---------------------------------
  '#202836', // paper-elevation-21
  // ---------------------------------
  '#252D3D', // paper-elevation-22
  '#252D3D', // paper-elevation-23
  // ---------------------------------
  '#2E394D' // paper-elevation-24
];

// Same as elevation 1 in all elevations
const darkThemeBoxShadow =
  '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)';

export const sharedThemeOptions: ThemeOptions = {
  palette: { ...colors },
  typography: {
    fontFamily: FONT_FAMILY,
    fontFamilySecondary: FONT_FAMILY_SECONDARY,
    roboto: FONT_FAMILY_ROBOTO,
    robotoMono: FONT_FAMILY_ROBOTO_MONO,
    dejavuMono: FONT_FAMILY_DEJAVU_MONO,
    sourceCodePro: FONT_FAMILY_SOURCE_CODE_PRO,
    sourceSansPro: FONT_FAMILY,
    fontWeightMedium: 600,
    h1: {
      fontFamily: FONT_FAMILY_SECONDARY
    },
    h2: {
      fontFamily: FONT_FAMILY_SECONDARY
    },
    h3: {
      fontFamily: FONT_FAMILY_SECONDARY
    },
    h4: {
      fontFamily: FONT_FAMILY_SECONDARY
    },
    h5: {
      fontFamily: FONT_FAMILY_SECONDARY
    },
    h6: {
      fontFamily: FONT_FAMILY_SECONDARY
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1025,
      xl: 1536
    }
  },
  transitions: {
    duration: {
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
      short: 250,
      shorter: 200,
      shortest: 150,
      evenShortest: 100,
      standard: 300
    }
  },
  components: {
    // In v7 cells are not flex by default anymore, add this and other styles for backwards compatibility. https://mui.com/x/migration/migration-data-grid-v6/#css-classes-and-styling
    MuiDataGrid: {
      styleOverrides: {
        root: {
          [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: { outline: 'transparent' },
          [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none'
          }
        },
        cell: {
          display: 'flex',
          alignItems: 'center',
          lineHeight: 'normal'
        }
      },
      defaultProps: {
        sortingOrder: ['desc', 'asc'],
        slots: {
          detailPanelExpandIcon: ExpandMoreTwoTone as DataGrid8TemporalIconFix,
          detailPanelCollapseIcon: ExpandLessTwoTone as DataGrid8TemporalIconFix
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 600
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        sizeSmall: {
          padding: 4
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        InputProps: {
          size: 'small'
        },
        SelectProps: {
          size: 'small'
        },
        InputLabelProps: {
          size: 'small'
        }
      }
    },
    MuiSelect: {
      defaultProps: {
        size: 'small'
      }
    },
    MuiAutocomplete: {
      defaultProps: {
        size: 'small'
      }
    }
  }
};

export const darkTheme = createTheme(
  deepmerge(sharedThemeOptions, {
    palette: {
      mode: 'dark',
      primary: {
        main: '#29B6F6',
        selected: alpha('#29B6F6', 0.16),
        shadow: alpha('#29B6F6', 0.25),
        dark: '#039BE5'
      },
      secondary: {
        main: '#9575CD',
        light: '#D1C4E9',
        dark: '#673AB7'
      },
      error: {
        main: '#F44336',
        light: '#F6685E',
        dark: '#D32F2F',
        hover: alpha('#F44336', 0.08),
        selected: alpha('#F44336', 0.16),
        alertColor: lighten('#F6685E', 0.6),
        alertBackground: darken('#F6685E', 0.9),
        focusVisible: alpha('#F44336', 0.3)
      },
      success: {
        main: '#66BB6A',
        light: '#81C784',
        dark: '#388E3C',
        hover: alpha('#66BB6A', 0.08),
        selected: alpha('#66BB6A', 0.16),
        alertColor: lighten('#81C784', 0.6),
        alertBackground: darken('#81C784', 0.9),
        focusVisible: alpha('#66BB6A', 0.3)
      },
      info: {
        main: '#29B6F6',
        light: '#4FC3F7',
        dark: '#0288D1',
        hover: alpha('#29B6F6', 0.08),
        selected: alpha('#29B6F6', 0.16),
        alertColor: lighten('#4FC3F7', 0.6),
        alertBackground: darken('#4FC3F7', 0.9),
        focusVisible: alpha('#29B6F6', 0.3)
      },
      warning: {
        main: '#FFA726',
        light: '#FFB851',
        dark: '#B2741A',
        hover: alpha('#FFA726', 0.08),
        selected: alpha('#FFA726', 0.16),
        alertColor: lighten('#FFB851', 0.6),
        alertBackground: darken('#FFB851', 0.9),
        focusVisible: alpha('#FFA726', 0.3)
      },
      background: {
        default: '#121722',
        ...paperElevationColors.reduce((acc, current, index) => {
          const key = index === 0 ? 'paper' : `paperElevation${index}`;

          acc[key] = current;
          return acc;
        }, {}),
        tooltip: alpha('#616161', 0.92)
      },
      action: {
        selectedOpacity: 0.16,
        active: alpha('#FFFFFF', 0.56),
        hover: alpha('#FFFFFF', 0.08),
        disabled: alpha('#FFFFFF', 0.38)
      },
      common: {
        black: '#000',
        white: '#FFF',
        whiteHover: alpha('#fff', 0.08),
        blackFocusVisible: alpha('#000', 0.3),
        whiteFocusVisible: alpha('#FFF', 0.3),
        blackSelected: alpha('#000', 0.16),
        blackHover: alpha('#000', 0.08),
        blackFocus: alpha('#000', 0.12),
        blackOutlinedBorder: alpha('#000', 0.5),
        whiteSelected: alpha('#fff', 0.16)
      }
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none'
          },
          elevation0: {
            backgroundColor: paperElevationColors[0]
          },
          elevation1: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[1]
          },
          elevation2: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[2]
          },
          elevation3: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[3]
          },
          elevation4: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[4]
          },
          elevation5: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[5]
          },
          elevation6: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[6]
          },
          elevation7: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[7]
          },
          elevation8: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[8]
          },
          elevation9: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[9]
          },
          elevation10: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[10]
          },
          elevation11: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[11]
          },
          elevation12: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[12]
          },
          elevation13: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[13]
          },
          elevation14: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[14]
          },
          elevation15: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[15]
          },
          elevation16: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[16]
          },
          elevation17: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[17]
          },
          elevation18: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[18]
          },
          elevation19: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[19]
          },
          elevation20: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[20]
          },
          elevation21: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[21]
          },
          elevation22: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[22]
          },
          elevation23: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[23]
          },
          elevation24: {
            boxShadow: darkThemeBoxShadow,
            backgroundColor: paperElevationColors[24]
          }
        }
      }
    }
  } satisfies ThemeOptions)
);

const lightTheme = createTheme(deepmerge(sharedThemeOptions, DEFAULT_THEME satisfies ThemeOptions));

const cache = createCache({
  key: 'ai',
  prepend: true // prepend ensures that @emotion/css classnames are injected after @mui/material and @emotion/react ones
});

interface AiAppThemeProps {
  children: React.ReactNode;
  mode?: PaletteMode;
}

export function AiAppTheme({ children, mode = 'light' }: AiAppThemeProps) {
  const theme = useMemo(() => (mode === 'dark' ? darkTheme : lightTheme), [mode]);

  window['theme'] = theme; // Expose theme globally for debugging

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <GlobalStyles
          styles={{
            body: {
              width: '100vw',
              height: '100vh',

              '& #root': {
                height: '100%',
                width: '100%'
              }
            },
            a: {
              textDecoration: 'none'
            },
            'ol, ul': {
              paddingInlineStart: 24
            }
          }}
        />
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
}
